import { useState } from "react";
import { Clock, LogOut, ShieldCheck, Bell, BellRing, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { BrandLockup } from "@/components/BrandLockup";

const pushSupported = () =>
  typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

function NotifyWhenApproved() {
  const [on, setOn] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!pushSupported()) return null;

  const enable = async () => {
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") { toast.error("Notifications blocked. Enable them in your device settings."); return; }
      const reg = await navigator.serviceWorker.ready;
      const { key } = await api.pushVapidKey();
      if (!key) { toast.error("Push isn't available right now."); return; }
      let sub = await reg.pushManager.getSubscription();
      if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(key) });
      const j = sub.toJSON();
      await api.pushSubscribe({ endpoint: j.endpoint, keys: j.keys });
      setOn(true);
      toast.success("Great — we'll buzz you the moment you're approved.");
    } catch (e) {
      toast.error("Couldn't turn that on. On iPhone, add Stage Door to your Home Screen first.");
    } finally {
      setBusy(false);
    }
  };

  if (on) {
    return (
      <div className="mt-6 flex items-center justify-center gap-2 text-sm text-emerald-400" data-testid="pending-notify-on">
        <BellRing className="h-4 w-4" /> You'll get a push when you're approved.
      </div>
    );
  }
  return (
    <button
      onClick={enable}
      disabled={busy}
      data-testid="pending-notify-btn"
      className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-[#9B2226] px-4 py-2.5 text-sm font-600 text-white transition-colors hover:bg-[#7A1A1E]"
    >
      <Bell className="h-4 w-4" /> {busy ? "Turning on…" : "Notify me when I'm approved"}
    </button>
  );
}

function DownloadMyData() {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const blob = await api.exportMyData();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `stage-door-tours-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Your data is downloading as a CSV.");
    } catch {
      toast.error("Couldn't download your data. Please try again.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      onClick={download}
      disabled={busy}
      data-testid="download-my-data-btn"
      className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-[#9B2226] px-4 py-2.5 text-sm font-600 text-white transition-colors hover:bg-[#7A1A1E] disabled:opacity-50"
    >
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} {busy ? "Preparing…" : "Download my data (CSV)"}
    </button>
  );
}

export default function PendingApproval() {
  const { user, logout } = useAuth();
  const denied = user?.status === "denied";

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-zinc-950 px-6" data-testid="pending-approval-screen">
      <div className="pointer-events-none absolute inset-0 bg-cover bg-center opacity-40" style={{ backgroundImage: "url(/logo.jpg)" }} />
      <div className="pointer-events-none absolute inset-0 plaid-veil" />

      <div className="relative w-full max-w-md rounded-2xl lg-glass border border-[#9B2226]/40 p-8 text-center">
        <div className="mb-6 flex justify-center">
          <BrandLockup size="lg" testid="pending-brand" />
        </div>
        <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-[#9B2226]/15 text-[#E0575B]">
          {denied ? <ShieldCheck className="h-7 w-7" /> : <Clock className="h-7 w-7" />}
        </div>

        <h1 className="font-display text-2xl font-800 tracking-tight text-zinc-50" data-testid="pending-title">
          {denied ? "Access paused" : "Pending approval"}
        </h1>

        <p className="mt-3 text-sm text-zinc-300" data-testid="pending-message">
          {denied ? (
            <>Your access to Stage Door has been paused. You can still download a copy of your data below. If you think this is a mistake, reach out to the owner.</>
          ) : (
            <>Thanks for signing in{user?.name ? `, ${user.name.split(" ")[0]}` : ""}. Your account is waiting for the owner to approve access. You'll be able to sign in and use the app once you're approved.</>
          )}
        </p>

        {user?.email && (
          <div className="mt-5 inline-flex items-center gap-2 rounded-full border border-zinc-700 bg-zinc-900 px-3 py-1.5 text-xs text-zinc-300" data-testid="pending-email">
            Signed in as <span className="font-600 text-zinc-100">{user.email}</span>
          </div>
        )}

        {!denied && <NotifyWhenApproved />}
        {denied && <DownloadMyData />}

        <button
          onClick={logout}
          className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-zinc-700 bg-zinc-900 px-4 py-2.5 text-sm font-600 text-zinc-200 transition-colors hover:bg-zinc-800"
          data-testid="pending-logout-btn"
        >
          <LogOut className="h-4 w-4" /> Sign out
        </button>

        <p className="mt-6 text-[11px] leading-relaxed text-zinc-400" data-testid="pending-disney-disclaimer">
          Stage Door is an independent personal tool and is not affiliated with, endorsed by, or associated with The Walt Disney Company.
        </p>
      </div>
    </div>
  );
}
